import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AuthContext from '../../context/authContext';
import { NumberChip } from '../Bookings/components/BookingIcons';
import useSuspiciousRequestIncidents from '../../hooks/useSuspiciousRequestIncidents';
import {
  getBarTooltipCursor,
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsLegendBarProps,
  getRechartsTooltipProps,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  viewRootTextPrimarySx,
} from '../../util/chartTheme';

const MENU_ITEM_NAME = 'IP-Audit';
const PAGE_SIZE = 25;
const AUTO_REFRESH_MS = 60_000;
const STATS_RANGE_OPTIONS = [
  { value: 6, label: '6 h' },
  { value: 24, label: '24 h' },
  { value: 72, label: '3 T' },
  { value: 168, label: '7 T' },
];

const integerFormatter = new Intl.NumberFormat('de-DE');
const decimalFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const percentFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatInteger = value => integerFormatter.format(Math.round(Number(value || 0)));
const formatPercent = value => `${percentFormatter.format(Number(value || 0) * 100)} %`;
const formatDecimal = value => decimalFormatter.format(Number(value || 0));

const formatDurationMs = value => {
  const ms = Math.max(0, Math.round(Number(value || 0)));
  if (ms < 1000) return `${integerFormatter.format(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${decimalFormatter.format(seconds)} s`;
  return `${decimalFormatter.format(seconds / 60)} min`;
};

const formatGranularityLabel = minutes => {
  const m = Number(minutes || 0);
  if (m <= 0) return '—';
  if (m < 60) return `${m} Min`;
  if (m === 60) return '1 h';
  return `${m / 60} h`;
};

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE');
}

function formatDurationWindow(incident) {
  const from = formatDateTime(incident?.firstSeenAt);
  const to = formatDateTime(incident?.lastSeenAt);
  return `${from} bis ${to}`;
}

function SuspiciousRequestAuditView() {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const {
    isLoading,
    fetchIncidents,
    fetchRequestStats,
    fetchIncidentDetails,
    acknowledgeIncident,
    blacklistIncidentIp,
  } = useSuspiciousRequestIncidents();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [detailDialog, setDetailDialog] = useState({ open: false, incident: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [statsRangeHours, setStatsRangeHours] = useState(24);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [endpointClassification, setEndpointClassification] = useState('internal');
  const [endpointSort, setEndpointSort] = useState({ key: 'sumMs', dir: 'desc' });

  const canRead = authCtx.checkRight('securityAudit:read');
  const canAcknowledge = authCtx.checkRight('securityAudit:acknowledge');
  const canBlacklist = authCtx.checkRight('securityAudit:blacklist');

  const fetchIncidentsRef = useRef(fetchIncidents);
  fetchIncidentsRef.current = fetchIncidents;
  const fetchRequestStatsRef = useRef(fetchRequestStats);
  fetchRequestStatsRef.current = fetchRequestStats;
  const refreshCountForMenuItemRef = useRef(authCtx.refreshCountForMenuItem);
  refreshCountForMenuItemRef.current = authCtx.refreshCountForMenuItem;

  const loadRef = useRef(async () => {});
  const paginationPageRef = useRef(1);

  const load = useCallback(async (page = 1) => {
    try {
      const response = await fetchIncidentsRef.current({ page, limit: PAGE_SIZE });
      setItems(Array.isArray(response?.incidents) ? response.incidents : []);
      setPagination(response?.pagination || { page, totalPages: 1, total: 0 });
      refreshCountForMenuItemRef.current?.(MENU_ITEM_NAME);
    } catch {
      /* Toast is shown inside the hook */
    }
  }, []);

  loadRef.current = load;

  const loadStats = useCallback(async hours => {
    setStatsLoading(true);
    try {
      const response = await fetchRequestStatsRef.current({ hours });
      setStats(response || null);
    } catch {
      /* Toast is shown inside the hook */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadStatsRef = useRef(loadStats);
  loadStatsRef.current = loadStats;
  const statsRangeHoursRef = useRef(statsRangeHours);
  statsRangeHoursRef.current = statsRangeHours;

  useEffect(() => {
    paginationPageRef.current = pagination.page || 1;
  }, [pagination.page]);

  useEffect(() => {
    if (!canRead || !authCtx.token) return;
    load(1);
  }, [canRead, authCtx.token, load]);

  useEffect(() => {
    if (!canRead || !authCtx.token) return;
    loadStats(statsRangeHours);
  }, [canRead, authCtx.token, statsRangeHours, loadStats]);

  useEffect(() => {
    if (!canRead || !authCtx.token) return undefined;
    const id = window.setInterval(() => {
      loadRef.current(paginationPageRef.current);
      loadStatsRef.current(statsRangeHoursRef.current);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [canRead, authCtx.token]);

  const openDetails = useCallback(
    async item => {
      if (!item?._id) return;
      setDetailDialog({ open: true, incident: null });
      setDetailLoading(true);
      try {
        const response = await fetchIncidentDetails(item._id);
        setDetailDialog({ open: true, incident: response?.incident || item });
      } finally {
        setDetailLoading(false);
      }
    },
    [fetchIncidentDetails]
  );

  const closeDetails = useCallback(() => {
    setDetailDialog({ open: false, incident: null });
  }, []);

  const handleAcknowledge = useCallback(
    async item => {
      if (!item?._id) return;
      setActionLoadingId(item._id);
      try {
        await acknowledgeIncident(item._id);
        await load(pagination.page);
      } finally {
        setActionLoadingId(null);
      }
    },
    [acknowledgeIncident, load, pagination.page]
  );

  const handleBlacklist = useCallback(
    async item => {
      if (!item?._id) return;
      setActionLoadingId(item._id);
      try {
        await blacklistIncidentIp(item._id);
        await load(pagination.page);
      } finally {
        setActionLoadingId(null);
      }
    },
    [blacklistIncidentIp, load, pagination.page]
  );

  const handleStatsRangeChange = useCallback((_event, value) => {
    if (value) setStatsRangeHours(value);
  }, []);

  const handleEndpointClassificationChange = useCallback((_event, value) => {
    if (value) setEndpointClassification(value);
  }, []);

  const handleEndpointSort = useCallback(key => {
    setEndpointSort(prev => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      const isText = key === 'method' || key === 'path';
      return { key, dir: isText ? 'asc' : 'desc' };
    });
  }, []);

  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartTooltipProps = useMemo(() => getRechartsTooltipProps(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const barTooltipCursor = useMemo(() => getBarTooltipCursor(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);

  const histogramData = useMemo(() => {
    const buckets = Array.isArray(stats?.buckets) ? stats.buckets : [];
    const totalHours = buckets.length;
    const labelFormat = totalHours > 48 ? 'DD.MM.' : totalHours > 12 ? 'DD.MM. HH' : 'HH:mm';
    return buckets.map(b => ({
      ...b,
      label: dayjs(b.hourStart).format(labelFormat),
    }));
  }, [stats]);

  const responseTimeData = useMemo(() => {
    const buckets = Array.isArray(stats?.responseTime?.buckets) ? stats.responseTime.buckets : [];
    if (buckets.length === 0) return [];
    const granularity = stats?.responseTime?.granularityMinutes || 15;
    const labelFormat = granularity >= 60
      ? buckets.length > 24
        ? 'DD.MM. HH'
        : 'HH:mm'
      : buckets.length > 24
        ? 'DD.MM. HH:mm'
        : 'HH:mm';
    return buckets.map(b => ({
      ...b,
      label: dayjs(b.from).format(labelFormat),
    }));
  }, [stats]);

  const responseTimeHasSamples = useMemo(() => {
    const total = stats?.responseTime?.totals?.sampleCount?.total;
    return Number(total || 0) > 0;
  }, [stats]);

  const topEndpointsRaw = useMemo(() => {
    const list = stats?.responseTime?.topEndpoints?.[endpointClassification];
    return Array.isArray(list) ? list : [];
  }, [stats, endpointClassification]);

  const sortedTopEndpoints = useMemo(() => {
    if (topEndpointsRaw.length === 0) return [];
    const list = topEndpointsRaw.slice();
    const { key, dir } = endpointSort;
    const direction = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av || '').localeCompare(String(bv || '')) * direction;
      }
      return ((Number(av) || 0) - (Number(bv) || 0)) * direction;
    });
    return list;
  }, [topEndpointsRaw, endpointSort]);

  const endpointsTrackedCount = stats?.responseTime?.topEndpoints?.trackedEndpointCount || 0;
  const endpointsLimit = stats?.responseTime?.topEndpoints?.limit || 50;

  const kpiCards = useMemo(() => {
    const totals = stats?.totals;
    if (!totals) return [];
    const peakStart = totals.peakHourStart ? dayjs(totals.peakHourStart) : null;
    const rangeHours = stats?.rangeHours || statsRangeHours;
    const rt = stats?.responseTime?.totals;
    const cards = [
      {
        label: 'API-Anfragen gesamt',
        value: formatInteger(totals.total),
        helper: `Letzte ${rangeHours} Stunden`,
      },
      {
        label: 'Interne Endpunkte',
        value: formatInteger(totals.internal),
        helper: totals.total > 0 ? `${formatPercent(totals.internalShare)} aller Anfragen` : 'Authentifiziert',
      },
      {
        label: 'Öffentliche Endpunkte',
        value: formatInteger(totals.public),
        helper: totals.total > 0 ? `${formatPercent(totals.publicShare)} aller Anfragen` : 'Ohne Authentifizierung',
      },
      {
        label: 'Ø Anfragen / Stunde',
        value: formatDecimal(totals.averagePerHour),
        helper: `${formatInteger(totals.total)} in ${rangeHours} Stunden`,
      },
      {
        label: 'Spitzenstunde',
        value: formatInteger(totals.peakHourTotal),
        helper: peakStart ? peakStart.format('DD.MM.YYYY HH:mm') : 'Keine Anfragen erfasst',
      },
    ];
    if (rt) {
      cards.push({
        label: 'Ø Antwortzeit (intern)',
        value: rt.sampleCount?.internal > 0 ? formatDurationMs(rt.internalAvgMs) : '—',
        helper:
          rt.sampleCount?.internal > 0
            ? `${formatInteger(rt.sampleCount.internal)} Messpunkte`
            : 'Keine Messwerte',
      });
      cards.push({
        label: 'Ø Antwortzeit (öffentlich)',
        value: rt.sampleCount?.public > 0 ? formatDurationMs(rt.publicAvgMs) : '—',
        helper:
          rt.sampleCount?.public > 0
            ? `${formatInteger(rt.sampleCount.public)} Messpunkte`
            : 'Keine Messwerte',
      });
    }
    return cards;
  }, [stats, statsRangeHours]);

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  const warningStripeSx = {
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `repeating-linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.12)} 0 12px, ${alpha(
        theme.palette.warning.main,
        0.12
      )} 12px 24px)`,
      pointerEvents: 'none',
    },
    '& > *': { position: 'relative' },
  };

  return (
    <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent='space-between'
      >
        <ToggleButtonGroup
          value={statsRangeHours}
          exclusive
          size='small'
          onChange={handleStatsRangeChange}
        >
          {STATS_RANGE_OPTIONS.map(option => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 1.5,
        }}
      >
        {kpiCards.length === 0
          ? Array.from({ length: 7 }).map((_, idx) => (
              <Box
                key={`kpi-skeleton-${idx}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  minHeight: 92,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='body2' sx={{ color: kpiLabelColor, fontSize: '0.85rem' }}>
                  &nbsp;
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {statsLoading ? '…' : '—'}
                </Typography>
                <Typography variant='caption' sx={{ color: kpiHelperColor }}>
                  &nbsp;
                </Typography>
              </Box>
            ))
          : kpiCards.map(card => (
              <Box
                key={card.label}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant='body2' sx={{ color: kpiLabelColor, fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                {card.helper ? (
                  <Typography variant='caption' sx={{ color: kpiHelperColor }}>
                    {card.helper}
                  </Typography>
                ) : null}
              </Box>
            ))}
      </Box>

      <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
        {histogramData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant='body2' color='text.secondary'>
              {statsLoading ? 'Lade Zugriffsstatistik …' : 'Keine Zugriffsdaten im gewählten Zeitraum.'}
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={histogramData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={chartGridStroke} vertical={false} />
              <XAxis dataKey='label' stroke={axisStroke} interval='preserveStartEnd' />
              <YAxis
                stroke={axisStroke}
                allowDecimals={false}
                tickFormatter={value => integerFormatter.format(value)}
              />
              <RechartsTooltip
                cursor={barTooltipCursor}
                {...chartTooltipProps}
                labelFormatter={(_label, payload) => {
                  const raw = payload?.[0]?.payload?.hourStart;
                  return raw ? dayjs(raw).format('DD.MM.YYYY HH:mm') : _label;
                }}
                formatter={(value, name) => [formatInteger(value), name]}
              />
              <Legend {...chartLegendBarProps} />
              <Bar
                stackId='requests'
                dataKey='internal'
                name='Intern (auth)'
                fill={theme.palette.primary.main}
              />
              <Bar
                stackId='requests'
                dataKey='public'
                name='Öffentlich'
                fill={theme.palette.warning.main}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Stack direction='row' alignItems='baseline' justifyContent='space-between' sx={{ mt: 1 }}>
        <Typography variant='subtitle2' sx={{ m: 0, fontWeight: 600 }}>
          Ø Antwortzeit
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {stats?.responseTime?.granularityMinutes
            ? `Bucket: ${formatGranularityLabel(stats.responseTime.granularityMinutes)}`
            : ''}
        </Typography>
      </Stack>

      <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 280 }}>
        {responseTimeData.length === 0 || !responseTimeHasSamples ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant='body2' color='text.secondary'>
              {statsLoading ? 'Lade Antwortzeiten …' : 'Keine Antwortzeit-Messwerte im gewählten Zeitraum.'}
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={responseTimeData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={chartGridStroke} vertical={false} />
              <XAxis dataKey='label' stroke={axisStroke} interval='preserveStartEnd' />
              <YAxis
                stroke={axisStroke}
                allowDecimals={false}
                tickFormatter={value => formatDurationMs(value)}
                width={70}
              />
              <RechartsTooltip
                {...chartTooltipProps}
                labelFormatter={(_label, payload) => {
                  const raw = payload?.[0]?.payload?.from;
                  return raw ? dayjs(raw).format('DD.MM.YYYY HH:mm') : _label;
                }}
                formatter={(value, name, ctx) => {
                  const sampleCount = ctx?.payload?.sampleCount;
                  const samples =
                    name === 'Intern (auth)'
                      ? sampleCount?.internal
                      : name === 'Öffentlich'
                        ? sampleCount?.public
                        : sampleCount?.total;
                  const formatted = formatDurationMs(value);
                  return [
                    samples ? `${formatted} (${formatInteger(samples)} Anfragen)` : formatted,
                    name,
                  ];
                }}
              />
              <Legend {...chartLegendBarProps} />
              <Line
                type='monotone'
                dataKey='internalAvgMs'
                name='Intern (auth)'
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
              <Line
                type='monotone'
                dataKey='publicAvgMs'
                name='Öffentlich'
                stroke={theme.palette.warning.main}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent='space-between'
        spacing={1}
        sx={{ mt: 1 }}
      >
        <Stack direction='row' alignItems='baseline' spacing={1}>
          <Typography variant='subtitle2' sx={{ m: 0, fontWeight: 600 }}>
            Langsamste Endpunkte
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Top {endpointsLimit} sortierbar
            {endpointsTrackedCount > 0
              ? ` · ${formatInteger(endpointsTrackedCount)} eindeutige Endpunkte erfasst`
              : ''}
          </Typography>
        </Stack>
        <ToggleButtonGroup
          value={endpointClassification}
          exclusive
          size='small'
          onChange={handleEndpointClassificationChange}
        >
          <ToggleButton value='internal'>Intern (auth)</ToggleButton>
          <ToggleButton value='public'>Öffentlich</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={endpointSort.key === 'method'}
                  direction={endpointSort.key === 'method' ? endpointSort.dir : 'asc'}
                  onClick={() => handleEndpointSort('method')}
                >
                  Methode
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={endpointSort.key === 'path'}
                  direction={endpointSort.key === 'path' ? endpointSort.dir : 'asc'}
                  onClick={() => handleEndpointSort('path')}
                >
                  Pfad
                </TableSortLabel>
              </TableCell>
              <TableCell align='right'>
                <TableSortLabel
                  active={endpointSort.key === 'count'}
                  direction={endpointSort.key === 'count' ? endpointSort.dir : 'desc'}
                  onClick={() => handleEndpointSort('count')}
                >
                  Anfragen
                </TableSortLabel>
              </TableCell>
              <TableCell align='right'>
                <TableSortLabel
                  active={endpointSort.key === 'avgMs'}
                  direction={endpointSort.key === 'avgMs' ? endpointSort.dir : 'desc'}
                  onClick={() => handleEndpointSort('avgMs')}
                >
                  Ø Antwortzeit
                </TableSortLabel>
              </TableCell>
              <TableCell align='right'>
                <TableSortLabel
                  active={endpointSort.key === 'maxMs'}
                  direction={endpointSort.key === 'maxMs' ? endpointSort.dir : 'desc'}
                  onClick={() => handleEndpointSort('maxMs')}
                >
                  Max
                </TableSortLabel>
              </TableCell>
              <TableCell align='right'>
                <TableSortLabel
                  active={endpointSort.key === 'sumMs'}
                  direction={endpointSort.key === 'sumMs' ? endpointSort.dir : 'desc'}
                  onClick={() => handleEndpointSort('sumMs')}
                >
                  Gesamtzeit
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTopEndpoints.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant='body2' color='text.secondary'>
                    {statsLoading
                      ? 'Lade Endpunkte …'
                      : 'Keine Endpunkt-Messwerte im gewählten Zeitraum.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedTopEndpoints.map((endpoint, idx) => (
                <TableRow key={`${endpoint.method}-${endpoint.path}-${idx}`} hover>
                  <TableCell sx={{ width: 88 }}>
                    <Chip
                      size='small'
                      label={endpoint.method || '?'}
                      sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                    >
                      {endpoint.path}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>{formatInteger(endpoint.count)}</TableCell>
                  <TableCell align='right'>{formatDurationMs(endpoint.avgMs)}</TableCell>
                  <TableCell align='right'>{formatDurationMs(endpoint.maxMs)}</TableCell>
                  <TableCell align='right'>{formatDurationMs(endpoint.sumMs)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mt: 1 }}
      >
        <Typography variant='subtitle1' sx={{ m: 0, fontWeight: 600 }}>
          Offene Auffälligkeiten
        </Typography>
      </Stack>

      <TableContainer component={Paper} sx={items.length > 0 ? warningStripeSx : undefined}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Endpunkt</TableCell>
              <TableCell>IP</TableCell>
              <TableCell align='right'>Anfragen</TableCell>
              <TableCell>Zeitfenster</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant='body2' color='text.secondary'>
                    Keine offenen IP-Audit-Einträge.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map(item => (
              <TableRow key={item._id} hover>
                <TableCell>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {item.method} {item.endpoint}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Schwelle: {item.threshold || 60} / {item.windowSeconds || 60}s
                  </Typography>
                </TableCell>
                <TableCell>{item.ip}</TableCell>
                <TableCell align='right'>
                  <NumberChip number={item.requestCount || 0} size='small' color='error.main' max={Infinity} />
                </TableCell>
                <TableCell>{formatDurationWindow(item)}</TableCell>
                <TableCell>
                  {item.blacklistExpiresAt ? (
                    <Chip
                      size='small'
                      color='error'
                      label={`Gesperrt bis ${formatDateTime(item.blacklistExpiresAt)}`}
                    />
                  ) : (
                    <Chip size='small' color='warning' label='Offen' />
                  )}
                </TableCell>
                <TableCell align='right'>
                  <Stack direction='row' gap={0.5} justifyContent='flex-end'>
                    <Tooltip title='Details anzeigen'>
                      <IconButton size='small' onClick={() => openDetails(item)}>
                        <VisibilityIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={canBlacklist ? 'IP für 24 Stunden sperren' : 'Recht fehlt'}>
                      <span>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => handleBlacklist(item)}
                          disabled={!canBlacklist || actionLoadingId === item._id}
                        >
                          <BlockIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canAcknowledge ? 'Eintrag abhaken' : 'Recht fehlt'}>
                      <span>
                        <IconButton
                          size='small'
                          color='success'
                          onClick={() => handleAcknowledge(item)}
                          disabled={!canAcknowledge || actionLoadingId === item._id}
                        >
                          <CheckCircleOutlineIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mt: 2 }}>
        <Typography variant='body2' color='text.secondary'>
          Seite {pagination.page || 1} von {pagination.totalPages || 1}, {pagination.total || 0} offen
        </Typography>
        <Stack direction='row' gap={1}>
          <Button
            variant='outlined'
            disabled={isLoading || (pagination.page || 1) <= 1}
            onClick={() => load((pagination.page || 1) - 1)}
          >
            Zurück
          </Button>
          <Button
            variant='outlined'
            disabled={isLoading || (pagination.page || 1) >= (pagination.totalPages || 1)}
            onClick={() => load((pagination.page || 1) + 1)}
          >
            Weiter
          </Button>
        </Stack>
      </Stack>

      <Dialog open={detailDialog.open} onClose={closeDetails} fullWidth maxWidth='lg'>
        <DialogTitle>IP-Audit Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant='body2'>Lade Details...</Typography>
          ) : (
            <Box>
              <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
                {detailDialog.incident?.method} {detailDialog.incident?.endpoint} · {detailDialog.incident?.ip}
              </Typography>
              <Box
                component='pre'
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor:
                    theme.palette.mode === 'light'
                      ? theme.palette.grey[100]
                      : alpha(theme.palette.common.white, 0.04),
                  border: `1px solid ${theme.palette.divider}`,
                  overflowX: 'auto',
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  maxHeight: '70vh',
                }}
              >
                {JSON.stringify(detailDialog.incident || {}, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {detailDialog.incident && (
            <>
              <Button
                color='error'
                startIcon={<BlockIcon />}
                onClick={() => handleBlacklist(detailDialog.incident)}
                disabled={!canBlacklist || actionLoadingId === detailDialog.incident._id}
              >
                IP 24h sperren
              </Button>
              <Button
                color='success'
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => {
                  handleAcknowledge(detailDialog.incident);
                  closeDetails();
                }}
                disabled={!canAcknowledge || actionLoadingId === detailDialog.incident._id}
              >
                Abhaken
              </Button>
            </>
          )}
          <Button onClick={closeDetails}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SuspiciousRequestAuditView;
